import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Input } from '@components/Input'
import { TextArea } from '@components/TextArea'

import { LocalFile, RemoteFile, Zone } from '@types'

import arrowIcon from '@assets/images/arrow_up-icon.svg'
import copyIcon from '@assets/images/copy-icon.svg'
import trashIcon from '@assets/images/trash-icon.svg'

import { UploadFile } from '@components/UploadFile'
import { PopUpDelete } from '@components/PopUpDelete'

import {
  ArrowImg,
  ButtonArrowContainer,
  ButtonContainer,
  CardContainer,
  CardHeaderContainer,
  CardHeaderTitle,
  ZoneContainer,
} from './ZoneCard.styled'

interface Props {
  zone: Zone
  files: (LocalFile | RemoteFile)[]
  onUpdateZoneFile: (plans: (LocalFile | RemoteFile)[]) => void
  onUpdateZone: (name: string, description: string) => void
  onDuplicateZone: (name: string, description: string) => void
  onDeleteZone: () => void
}

export const ZoneCard = (props: Props): JSX.Element => {
  const { zone, onUpdateZoneFile, onUpdateZone, onDuplicateZone, onDeleteZone, files } = props

  const { t } = useTranslation()

  const [name, setName] = useState<string>(zone.name)
  const [description, setDescription] = useState<string>(zone.description)
  const [plans, setPlans] = useState<(LocalFile | RemoteFile)[]>(files)

  const [open, setOpen] = useState<boolean>(true)

  const [deletingFile, setDeletingFile] = useState<RemoteFile | undefined>(undefined)

  return (
    <CardContainer>
      <CardHeaderContainer>
        <ButtonArrowContainer
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()

            setOpen(!open)
          }}
        >
          <ArrowImg open={open} src={arrowIcon} />
        </ButtonArrowContainer>
        <CardHeaderTitle>{name}</CardHeaderTitle>
        <ButtonContainer
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()

            onDuplicateZone(name, description)
          }}
        >
          <img src={copyIcon} />
        </ButtonContainer>
        <ButtonContainer
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()

            onDeleteZone()
          }}
        >
          <img src={trashIcon} />
        </ButtonContainer>
      </CardHeaderContainer>

      {open && (
        <ZoneContainer>
          <Input
            autoFocus
            required
            label={t('modules_project-settings_pages_manage_zones_label_zone-name')}
            placeholder={t('modules_project-settings_pages_manage_zones_placeholder_zone-name')}
            value={name}
            onChange={(v) => {
              setName(v)

              onUpdateZone(v, description)
            }}
          />
          <TextArea
            label={t('modules_project-settings_pages_manage_zones_label_description')}
            placeholder={t('modules_project-settings_pages_manage_zones_placeholder_description')}
            value={description}
            onChange={(v) => {
              setDescription(v)

              onUpdateZone(name, v)
            }}
          />
          <UploadFile
            label={t('modules_project-settings_pages_manage_zones_label_zone_plans')}
            labelUpload={t('modules_project-settings_pages_add_building_label_upload_plans')}
            files={plans}
            setFiles={(_plans) => {
              const remoteFileDeleted = _plans.find(
                (plan) => Object.hasOwn(plan, 'deleted') && (plan as RemoteFile).deleted,
              ) as RemoteFile | undefined

              if (_plans.length == 1 && remoteFileDeleted) {
                setDeletingFile(remoteFileDeleted)
              } else {
                setPlans([..._plans])

                onUpdateZoneFile([..._plans])
              }
            }}
          />
        </ZoneContainer>
      )}

      {deletingFile && (
        <PopUpDelete
          title={t('modules_project-settings_pages_zoning_delete_plan_title')}
          description={t('modules_project-settings_pages_zoning_delete_plan_descripition', {
            name: deletingFile.name,
          })}
          onSubmit={() => {
            setPlans([deletingFile] as (RemoteFile | LocalFile)[])
            onUpdateZoneFile([deletingFile] as (RemoteFile | LocalFile)[])

            setDeletingFile(undefined)
          }}
          onClose={() => setDeletingFile(undefined)}
        />
      )}
    </CardContainer>
  )
}
